// Builds the customer-preferences fields sent for PayPlan and National Debtline (same API, same fields).
import type { CustomerPreferencesEntry, NotifyCustomerDebtAdvice } from "@/shared/api/types";
import { isValidPhoneNumber } from "@/shared/lib/utils/phoneNumber";

export type CallTime = "Anytime" | "Morning" | "Afternoon" | "Evening";

// What the PayPlan contact card holds: contact method, number or email, WhatsApp tick and call time.
export interface ContactDetails {
  method: "phone" | "email";
  mobile: string;
  email: string;
  whatsApp: boolean;
  callTime: CallTime | null; // null: "Choose a time" is open but no slot picked yet
}

// Sent as preferredTimeForNdl when the customer picks "Anytime"; the other values are Morning, Afternoon, Evening.
export const ANYTIME_API_VALUE = "anytime";

export interface PreferenceFields {
  preferredTimeForNdl: string | null;
  debtAdviceContactPreferenceNdl: "Y" | "N";
  voicemailConsentNdl: "Y" | "N";
  emailMeNdl: "Y" | "N";
  textMeNdl: "Y" | "N";
  whatsAppConsent: "Y" | "N";
}

// Same fields for PayPlan and National Debtline: phone (text "Y") or email, a call time for phone, and "Y" for WhatsApp only when ticked (never for NDL).
export function preferenceFields(details: ContactDetails, allowAnytime = true): PreferenceFields {
  const phone = details.method === "phone";
  return {
    // Without "Anytime" (National Debtline) a phone choice always carries one of the three slots.
    preferredTimeForNdl: phone
      ? details.callTime === "Anytime"
        ? allowAnytime
          ? ANYTIME_API_VALUE
          : null
        : details.callTime
      : null,
    debtAdviceContactPreferenceNdl: phone ? "N" : "Y",
    voicemailConsentNdl: "N",
    emailMeNdl: phone ? "N" : "Y",
    textMeNdl: phone ? "Y" : "N",
    whatsAppConsent: phone && details.whatsApp ? "Y" : "N",
  };
}

// The contact choice remembered between visits; number and email come from the profile.
export type ContactChoice = Pick<ContactDetails, "method" | "whatsApp" | "callTime"> & { savedAt: number };

// Stored per customer.
const choiceKey = (): string => `contactChoices:${JSON.parse(localStorage.getItem("customerData") || "{}")?.customerId ?? ""}`;

// Only well-formed stored choices are used, so stale or edited data cannot change how the customer is contacted.
const isChoice = (c: any): c is ContactChoice =>
  (c?.method === "phone" || c?.method === "email") &&
  typeof c.whatsApp === "boolean" &&
  (c.callTime === null || ["Anytime", "Morning", "Afternoon", "Evening"].includes(c.callTime)) &&
  typeof c.savedAt === "number";

// Remembered choices by company name.
export function readContactChoices(): Record<string, ContactChoice> {
  try {
    const stored = JSON.parse(localStorage.getItem(choiceKey()) || "{}") ?? {};
    return Object.fromEntries(Object.entries(stored).filter(([, c]) => isChoice(c))) as Record<string, ContactChoice>;
  } catch {
    return {};
  }
}

// Remember a company's contact choice.
export function saveContactChoice(companyName: string, details: ContactDetails): void {
  try {
    const { method, whatsApp, callTime } = details;
    localStorage.setItem(choiceKey(), JSON.stringify({ ...readContactChoices(), [companyName]: { method, whatsApp, callTime, savedAt: Date.now() } }));
  } catch {
    // storage unavailable: skip
  }
}

// Card details from a remembered choice, if its phone or email is still usable.
export function contactDetailsFromChoice(
  choice: ContactChoice | undefined,
  profile: { mobile: string; email: string },
): ContactDetails | undefined {
  if (!choice) return undefined;
  if (choice.method === "phone" ? !isValidPhoneNumber(profile.mobile) : profile.email.trim() === "") return undefined;
  return { method: choice.method, whatsApp: choice.whatsApp, callTime: choice.callTime, mobile: profile.mobile, email: profile.email };
}

// True when the customer saved this choice after the backend row was last updated, so it is the newer one.
export const choiceIsNewer = (choice: ContactChoice | undefined, backendUpdatedAt: string): boolean =>
  !!choice && choice.savedAt > Date.parse(backendUpdatedAt);

// True when a phone call has no usable call time: no slot picked after "Choose a time", or Anytime for NDL (which has no Anytime).
export const callTimeMissing = (details: ContactDetails | undefined, allowAnytime: boolean): boolean =>
  details?.method === "phone" && (details.callTime === null || (!allowAnytime && details.callTime === "Anytime"));

// PayPlan fields for the debt-advice notification: EMAIL, PHONE or WHATSAPP, plus the call time when the customer chose phone.
export function payPlanNotifyFields(details: ContactDetails | undefined): Pick<NotifyCustomerDebtAdvice, "payPlanChannel" | "payPlanCallTime"> {
  if (!details) return {};
  if (details.method === "email") return { payPlanChannel: "EMAIL" };
  return { payPlanChannel: details.whatsApp ? "WHATSAPP" : "PHONE", ...(details.callTime && { payPlanCallTime: details.callTime }) };
}

// Turns a saved preferences row back into card details (undefined when the chosen phone number is missing or invalid, or the email is missing).
export function contactDetailsFromRow(
  row: Partial<CustomerPreferencesEntry> & { whatsAppConsent?: string },
  profile: { mobile: string; email: string },
): ContactDetails | undefined {
  // A row saved before this card existed has no whatsAppConsent. Its email was NDL's old automatic "Email Me", not a choice, so ignore it and keep phone as the default.
  if (row.emailMeNdl === "Y" && !row.whatsAppConsent) return undefined;
  const method = row.emailMeNdl === "Y" ? "email" : "phone";
  if (method === "phone" ? !isValidPhoneNumber(profile.mobile) : profile.email.trim() === "") return undefined;
  const time = (row.preferredTimeForNdl ?? "").toLowerCase();
  const callTime: CallTime =
    time === "morning" ? "Morning" : time === "afternoon" ? "Afternoon" : time === "evening" ? "Evening" : "Anytime";
  return {
    method,
    mobile: profile.mobile,
    email: profile.email,
    whatsApp: method === "phone" && row.whatsAppConsent === "Y",
    callTime,
  };
}
